import PermissionButton from '../../../permissions/PermissionButton';
import React, { useMemo, useState } from 'react';
import { Card, Table, Input, DatePicker, Button, Space, Drawer, message, Tabs } from 'antd';
import dayjs from 'dayjs';
import { useFetchAllAvoirsAchat } from '../../../hooks/achatAvoirHooks';
import { createAvoirAchat, validateAvoirAchat } from '../../../models/achatAvoirAPI';
import { useFetchAllFours } from '../../../hooks/fourHooks';
import { getDefaultEntrepot } from '../../../utils/defaultEntrepot';

const { RangePicker } = DatePicker;

const AvoirAchatList = ({ onSelect }) => {
  const [fournisseur, setFournisseur] = useState('');
  const [refSearch, setRefSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [dateRange, setDateRange] = useState([null, null]);

  const [activeTab, setActiveTab] = useState('notValidated');

  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    frsSearch: fournisseur || undefined,
    refSearch: refSearch || undefined,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    status: activeTab,
  }), [pagination, fournisseur, refSearch, dateRange]);

  const { items, total, loading, refetch } = useFetchAllAvoirsAchat(params);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedFour, setSelectedFour] = useState(null);

  const handleCreate = async () => {
    try {
      if (!selectedFour) { message.error('Veuillez sélectionner un fournisseur'); return; }
      const defaultEntrepot = await getDefaultEntrepot();
      const res = await createAvoirAchat({
        CODE_FRS: selectedFour.code_frs,
        FOURNISSEUR: selectedFour.sociale,
        DATE_AVR: new Date(),
        CODE_ENT: defaultEntrepot || null,
      });
      message.success(`Avoir créé: ${res.data.REF_AVR_A || res.data.id}`);
      setIsCreateOpen(false);
      setSelectedFour(null);
      refetch();
    } catch (e) {
      message.error(e.message || 'Erreur création');
    }
  };

  const handleValidate = async (record) => {
    try { await validateAvoirAchat(record.id); message.success('Avoir validé'); refetch(); }
    catch (e) { message.error(e.message || 'Erreur validation'); }
  };

  const columns = [
    { title: 'Référence', dataIndex: 'REF_AVR_A', key: 'REF_AVR_A' },
    { title: 'Date', dataIndex: 'DATE_AVR', key: 'DATE_AVR', render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: 'Fournisseur', dataIndex: 'FOURNISSEUR', key: 'FOURNISSEUR' },
    { title: 'Montant HT', dataIndex: 'MNT_HT', key: 'MNT_HT' },
    { title: 'Montant TTC', dataIndex: 'MNT_TTC', key: 'MNT_TTC' },
    { title: 'Validé', dataIndex: 'VALIDER', key: 'VALIDER', render: (v) => v ? 'Oui' : 'Non' },
    { title: 'Actions', key: 'actions', render: (_, record) => (
      <Space>
        {!record.VALIDER && <PermissionButton permissionModule="purchaseReturns" permissionAction="validate" size="small" type="primary" onClick={() => handleValidate(record)}>Valider</PermissionButton>}
      </Space>
    ) },
  ];

  return (
    <Card title="Avoirs achat" extra={
      <div style={{ display: 'flex', gap: 8 }}>
        <Input placeholder="Fournisseur" value={fournisseur} onChange={e=>setFournisseur(e.target.value)} />
        <Input placeholder="REF" value={refSearch} onChange={e=>setRefSearch(e.target.value)} />
        <RangePicker value={dateRange} onChange={(v)=>setDateRange(v)} />
        <PermissionButton permissionModule="purchaseReturns" permissionAction="create" type="primary" onClick={()=> setIsCreateOpen(true)}>Nouvel avoir</PermissionButton>
      </div>
    }>
      <Tabs activeKey={activeTab} onChange={(k)=> { setActiveTab(k); setPagination(p=>({ ...p, current: 1 })); }}>
        <Tabs.TabPane tab="Enregistrés" key="notValidated" />
        <Tabs.TabPane tab="Validés" key="validated" />
      </Tabs>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          onChange: (current, pageSize) => setPagination({ current, pageSize })
        }}
        onRow={(record)=>({ onClick: ()=> onSelect && onSelect(record.id) })}
      />

      <Drawer
        title="Créer un avoir achat"
        width={900}
        onClose={() => { setIsCreateOpen(false); setSelectedFour(null); }}
        open={isCreateOpen}
        extra={
          <Space>
            <PermissionButton   onClick={() => { setIsCreateOpen(false); setSelectedFour(null); }}>Annuler</PermissionButton>
            <PermissionButton permissionModule="purchaseReturns" permissionAction="create" type="primary" onClick={handleCreate} disabled={!selectedFour}>Créer</PermissionButton>
          </Space>
        }
      >
        <div style={{ display:'grid', gap:12 }}>
          <div style={{ padding: '12px', background:'#f0f2f5', borderRadius: 6 }}>
            <div style={{ color: selectedFour ? '#000' : '#999', marginBottom: 6 }}>
              {selectedFour ? 'Fournisseur sélectionné' : 'Veuillez sélectionner un fournisseur'}
            </div>
            {selectedFour && (
              <div>
                <b>{selectedFour.sociale}</b> — Code: {selectedFour.code_frs}
              </div>
            )}
          </div>
          <SupplierPicker onPick={(fr)=> setSelectedFour(fr)} />
        </div>
      </Drawer>
    </Card>
  );
};

export default AvoirAchatList;

const SupplierPicker = ({ onPick }) => {
  const { Fours: fours, loading } = useFetchAllFours();
  const [search, setSearch] = useState('');
  const data = (fours || []).filter(f => {
    const q = (search || '').toLowerCase();
    return !q || f.sociale?.toLowerCase().includes(q) || f.code_frs?.toLowerCase().includes(q);
  });
  const columns = [
    { title: 'Code', dataIndex: 'code_frs', key: 'code_frs' },
    { title: 'Fournisseur', dataIndex: 'sociale', key: 'sociale' },
    { title: 'Pays', dataIndex: 'pays', key: 'pays' },
  ];
  return (
    <div>
      <Input placeholder="Rechercher fournisseur (code, nom)" value={search} onChange={e=>setSearch(e.target.value)} style={{ marginBottom: 8 }} />
      <Table
        size="small"
        rowKey="code_frs"
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={{ pageSize: 8 }}
        onRow={(record)=> ({ onClick: ()=> onPick && onPick(record), style: { cursor:'pointer' } })}
      />
    </div>
  );
};
